import { useEffect, useRef, useState } from "react";
import type { Product } from "@/mocks/products";

interface Props {
  product: Product;
}

const tabs = ["商品介紹", "規格說明", "能量意涵", "購買須知"];

const categoryEnergy: Record<string, { title: string; desc: string; points: string[] }> = {
  dzi: {
    title: "天珠的能量意涵",
    desc: "天珠是藏傳佛教中最神聖的護身符之一，每一顆天珠都承載著千年的文化與信仰能量。",
    points: [
      "天珠的眼數代表不同的能量屬性，眼數越多能量越強",
      "佩戴天珠前建議先以清水或月光淨化，讓天珠與您的能量頻率同步",
      "天珠與佩戴者之間會建立獨特的能量連結，越戴越有靈性",
      "建議貼近皮膚佩戴，讓天珠的能量直接與您的氣場互動",
    ],
  },
  crystal: {
    title: "水晶的能量意涵",
    desc: "天然水晶是大自然億萬年孕育的能量寶石，每種水晶都有其獨特的振動頻率與能量屬性。",
    points: [
      "水晶的能量來自其內部規則的晶體結構，能夠儲存、放大與傳導能量",
      "定期以月光、陽光或水晶簇淨化，保持水晶能量的純淨",
      "佩戴時保持正向的意念，水晶能夠放大您的意念能量",
      "不同水晶適合放置在不同位置，以達到最佳的能量效果",
    ],
  },
  jade: {
    title: "玉石的能量意涵",
    desc: "玉石在中華文化中象徵君子之德，溫潤的質感蘊含著深厚的文化底蘊與能量。",
    points: [
      "玉石具有溫和穩定的能量，適合長期佩戴以達到身心平衡",
      "翡翠的綠色對應心輪，有助於促進愛與慈悲的能量流動",
      "玉石越戴越溫潤，與佩戴者的能量融合後會更加靈性",
      "建議以溫水輕輕清潔，避免化學清潔劑損傷玉石表面",
    ],
  },
  thangka: {
    title: "唐卡佛牌的能量意涵",
    desc: "唐卡是藏傳佛教的神聖藝術，每一幅唐卡都是藝術家在虔誠的修行狀態下完成的，蘊含著強大的加持能量。",
    points: [
      "唐卡需供奉在清淨的環境中，定期上香以維持神聖的能量場",
      "面對唐卡冥想或祈禱，能夠獲得菩薩的加持與守護",
      "佛牌可隨身佩戴，讓菩薩的能量時刻守護您",
      "唐卡的顏色與圖案都有其特定的象徵意義，代表不同的能量屬性",
    ],
  },
};

const specs: Record<string, { label: string; value: string }[]> = {
  dzi: [
    { label: "材質", value: "天然瑪瑙（天珠）" },
    { label: "珠徑", value: "8mm / 10mm / 12mm（可選）" },
    { label: "手圍", value: "約 16-18cm（可客製）" },
    { label: "配件", value: "925純銀扣頭" },
    { label: "產地", value: "西藏 / 尼泊爾" },
    { label: "附件", value: "精美禮盒 + 能量說明卡" },
  ],
  crystal: [
    { label: "材質", value: "天然水晶礦石" },
    { label: "珠徑", value: "8mm / 10mm / 12mm（可選）" },
    { label: "手圍", value: "約 16-18cm（可客製）" },
    { label: "配件", value: "925純銀扣頭" },
    { label: "產地", value: "巴西 / 馬達加斯加" },
    { label: "附件", value: "精美禮盒 + 能量說明卡" },
  ],
  jade: [
    { label: "材質", value: "天然翡翠 / 白玉" },
    { label: "尺寸", value: "依款式而定（詳見商品說明）" },
    { label: "顏色", value: "天然色澤（略有差異屬正常）" },
    { label: "配件", value: "18K金 / 925純銀配件" },
    { label: "產地", value: "緬甸 / 中國" },
    { label: "附件", value: "精美禮盒 + 鑑定書 + 能量說明卡" },
  ],
  thangka: [
    { label: "材質", value: "礦物顏料 / 金粉（手繪）" },
    { label: "尺寸", value: "依款式而定（詳見商品說明）" },
    { label: "畫布", value: "傳統棉布（唐卡專用）" },
    { label: "製作", value: "藏族傳統工藝師手繪" },
    { label: "產地", value: "西藏 / 尼泊爾" },
    { label: "附件", value: "精美包裝 + 文化說明書 + 收藏證書" },
  ],
};

export default function ProductDetailInfo({ product }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const energy = categoryEnergy[product.category];
  const specList = specs[product.category];

  return (
    <section className="py-16 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div
          className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Tabs */}
          <div className="flex items-center gap-0 border-b border-[#e8e0d5] mb-10 overflow-x-auto">
            {tabs.map((tab, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`px-6 py-4 text-[1rem] font-medium whitespace-nowrap cursor-pointer transition-all duration-200 border-b-2 -mb-px ${
                  activeTab === i
                    ? "border-[#8b6f47] text-[#8b6f47]"
                    : "border-transparent text-[#7a6a5a] hover:text-[#2d2520]"
                }`}
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[300px]">
            {/* 商品介紹 */}
            {activeTab === 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3
                    className="text-[#2d2520] font-bold mb-5 leading-[1.4]"
                    style={{ fontSize: "1.5rem", fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {product.name}
                  </h3>
                  <p
                    className="text-[#7a6a5a] leading-[1.85] mb-6 text-[1.0625rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {product.detail}
                  </p>
                  <p
                    className="text-[#7a6a5a] leading-[1.85] text-[1.0625rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    瓏靖的每一件商品都經過嚴格的品質把關，從原料的選取到工藝的製作，每一個環節都力求完美。我們相信，真正的能量飾品不只是外觀的美麗，更是內在能量的純粹與真實。
                  </p>
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-video">
                  <img
                    src={product.img}
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e1a16]/40 to-transparent"></div>
                </div>
              </div>
            )}

            {/* 規格說明 */}
            {activeTab === 1 && (
              <div className="max-w-2xl">
                <h3
                  className="text-[#2d2520] font-bold mb-6"
                  style={{ fontSize: "1.375rem", fontFamily: "'Noto Serif TC', serif" }}
                >
                  商品規格
                </h3>
                <div className="border border-[#e8e0d5] rounded-2xl overflow-hidden">
                  {specList.map((s, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-6 px-7 py-4 ${
                        i % 2 === 0 ? "bg-[#faf8f4]" : "bg-white"
                      } ${i < specList.length - 1 ? "border-b border-[#e8e0d5]" : ""}`}
                    >
                      <span
                        className="text-[#8b6f47] font-medium text-[0.9375rem] w-20 flex-shrink-0"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {s.label}
                      </span>
                      <span
                        className="text-[#5a4e45] text-[0.9375rem] leading-[1.6]"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {s.value}
                      </span>
                    </div>
                  ))}
                </div>
                <p
                  className="mt-5 text-[#a89880] text-[0.875rem] leading-[1.75]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  ※ 天然礦石因產地與批次不同，顏色與紋路略有差異，此為天然材質的特性，不影響品質與能量。
                </p>
              </div>
            )}

            {/* 能量意涵 */}
            {activeTab === 2 && (
              <div className="max-w-3xl">
                <h3
                  className="text-[#2d2520] font-bold mb-4"
                  style={{ fontSize: "1.375rem", fontFamily: "'Noto Serif TC', serif" }}
                >
                  {energy.title}
                </h3>
                <p
                  className="text-[#7a6a5a] leading-[1.85] mb-8 text-[1.0625rem]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {energy.desc}
                </p>
                <div className="space-y-4">
                  {energy.points.map((point, i) => (
                    <div key={i} className="flex items-start gap-4 p-5 bg-[#faf8f4] rounded-xl border border-[#e8e0d5]">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#f0e8dc] flex-shrink-0 mt-0.5">
                        <i className="ri-sparkling-2-line text-[#8b6f47] text-sm"></i>
                      </div>
                      <p
                        className="text-[#5a4e45] leading-[1.75] text-[0.9375rem]"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {point}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 購買須知 */}
            {activeTab === 3 && (
              <div className="max-w-3xl space-y-6">
                {[
                  {
                    icon: "ri-truck-line",
                    title: "配送說明",
                    content: "全台灣本島免運費，離島地區運費另計。訂單確認後 1-3 個工作天出貨，宅配到府約 1-2 天送達。",
                  },
                  {
                    icon: "ri-exchange-line",
                    title: "退換貨政策",
                    content: "收到商品後 7 天內，如有品質問題可申請退換貨。請保持商品完整包裝，並附上購買憑證。客製化商品恕不退換。",
                  },
                  {
                    icon: "ri-gift-2-line",
                    title: "包裝說明",
                    content: "所有商品均附精美禮盒包裝，適合作為禮物贈送。如需特殊包裝或附上祝福卡片，請在訂單備註中說明。",
                  },
                  {
                    icon: "ri-customer-service-2-line",
                    title: "售後服務",
                    content: "購買後如有任何問題，歡迎透過 LINE 或電子郵件聯繫我們。我們提供終身的能量諮詢服務，讓您的飾品始終保持最佳狀態。",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-5 p-6 bg-[#faf8f4] rounded-2xl border border-[#e8e0d5]">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#f0e8dc] flex-shrink-0">
                      <i className={`${item.icon} text-[#8b6f47] text-xl`}></i>
                    </div>
                    <div>
                      <h4
                        className="text-[#2d2520] font-semibold mb-2 text-[1rem]"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {item.title}
                      </h4>
                      <p
                        className="text-[#7a6a5a] leading-[1.75] text-[0.9375rem]"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {item.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
